import { type ReactNode, useRef, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { focusRing } from './primitives';

/**
 * Tabs, the design system's way and WAI-ARIA's: a `tablist` whose selected tab is the one
 * tab stop (roving tabindex), arrow keys that move **and** select, Home and End, and a
 * `tabpanel` per tab labelled by it. Caps labels, a hairline under the row, and an
 * underline in `fg.default` under the selected tab. Panels swap without sliding.
 *
 * Controlled: which tab is selected is the caller's state, so it can reset it — the design
 * opens every seller on Overview.
 */

export interface TabItem {
  id: string;
  label: string;
  disabled?: boolean;
}

/**
 * The tab a key moves to, or nothing when the key is not the tablist's. Left and right
 * wrap; Home and End go to the ends; a disabled tab is skipped, never landed on.
 */
export function tabStep(key: string, tabs: readonly TabItem[], current: string): string | undefined {
  const live = tabs.filter((tab) => !tab.disabled);
  if (live.length === 0) return undefined;
  const index = live.findIndex((tab) => tab.id === current);
  switch (key) {
    case 'ArrowRight':
      return live[(index + 1) % live.length]?.id;
    case 'ArrowLeft':
      return live[(index - 1 + live.length) % live.length]?.id;
    case 'Home':
      return live[0]?.id;
    case 'End':
      return live[live.length - 1]?.id;
    default:
      return undefined;
  }
}

const tabId = (idBase: string, tab: string) => `${idBase}-tab-${tab}`;
const panelId = (idBase: string, tab: string) => `${idBase}-panel-${tab}`;

export function Tabs({
  label,
  tabs,
  value,
  onChange,
  idBase,
}: {
  /** What the tabs are, for a screen reader: the page header's are "Page sections". */
  label: string;
  tabs: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Ties each tab to its `TabPanel`: pass the same one to both. */
  idBase: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{
        display: 'flex',
        gap: cssVar('space.lg'),
        overflowX: 'auto',
        borderBottom: `1px solid ${cssVar('color.border.default')}`,
      }}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          tab={tab}
          selected={tab.id === value}
          id={tabId(idBase, tab.id)}
          controls={panelId(idBase, tab.id)}
          tabRef={(element) => {
            refs.current[tab.id] = element;
          }}
          onSelect={() => onChange(tab.id)}
          onKey={(key) => {
            const next = tabStep(key, tabs, value);
            if (next === undefined) return false;
            onChange(next);
            refs.current[next]?.focus();
            return true;
          }}
        />
      ))}
    </div>
  );
}

function Tab({
  tab,
  selected,
  id,
  controls,
  tabRef,
  onSelect,
  onKey,
}: {
  tab: TabItem;
  selected: boolean;
  id: string;
  controls: string;
  tabRef: (element: HTMLButtonElement | null) => void;
  onSelect: () => void;
  /** Whether the tablist took the key. */
  onKey: (key: string) => boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const lit = !tab.disabled && (selected || hovered);
  return (
    <button
      ref={tabRef}
      id={id}
      type="button"
      role="tab"
      aria-selected={selected}
      // Only the selected tab's panel is on the page, so only it can be pointed at.
      aria-controls={selected ? controls : undefined}
      aria-disabled={tab.disabled ? true : undefined}
      tabIndex={selected ? 0 : -1}
      onClick={tab.disabled ? undefined : onSelect}
      onKeyDown={(event) => {
        if (onKey(event.key)) event.preventDefault();
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        flexShrink: 0,
        minHeight: cssVar('size.target.min'),
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: cssVar(lit ? 'color.fg.default' : 'color.fg.muted'),
        opacity: tab.disabled ? 0.5 : 1,
        cursor: tab.disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase',
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.label.size'),
        fontWeight: cssVar('type.title.weight'),
        lineHeight: cssVar('type.label.line'),
        // The underline sits inside the tab, over the list's hairline.
        boxShadow: selected
          ? `inset 0 calc(-1 * ${cssVar('size.tab.indicator')}) 0 ${cssVar('color.fg.default')}`
          : 'none',
        ...(focused ? focusRing : {}),
      }}
    >
      {tab.label}
    </button>
  );
}

/** The content under a tab. Pass the `idBase` the `Tabs` got, and the selected tab's id. */
export function TabPanel({ idBase, tab, children }: { idBase: string; tab: string; children: ReactNode }) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      role="tabpanel"
      id={panelId(idBase, tab)}
      aria-labelledby={tabId(idBase, tab)}
      // The panel is a stop of its own, so a keyboard reaches its content straight from the
      // tabs — the WAI-ARIA tabs pattern, for a panel that may open on text with no control in it.
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the tabs pattern asks for it
      tabIndex={0}
      onFocus={(event) => setFocused(event.target === event.currentTarget && event.target.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={focused ? focusRing : { outline: 'none' }}
    >
      {children}
    </div>
  );
}
